import { Store, useStore } from '@tanstack/react-store'

const LOCAL_STORAGE_KEY = 'cortado_local_settings'

export type Theme = 'light' | 'dark' | 'system'

interface LocalSettingsState {
  selectedOrganizationId: string | null
  theme: Theme
  aiSidebarWidth: number | null
}

// Load initial state from localStorage
function loadFromStorage(): LocalSettingsState {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.warn('Failed to load local settings:', e)
  }
  return { selectedOrganizationId: null, theme: 'system', aiSidebarWidth: null }
}

// Save state to localStorage
function saveToStorage(state: LocalSettingsState) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state))
  } catch (e) {
    console.warn('Failed to save local settings:', e)
  }
}

export const localSettingsStore = new Store<LocalSettingsState>(loadFromStorage())

export const localSettingsActions = {
  setSelectedOrganizationId: (id: string | null) => {
    localSettingsStore.setState((state) => {
      const newState = { ...state, selectedOrganizationId: id }
      saveToStorage(newState)
      return newState
    })
  },
  setTheme: (theme: Theme) => {
    localSettingsStore.setState((state) => {
      const newState = { ...state, theme }
      saveToStorage(newState)
      return newState
    })
  },
  setAISidebarWidth: (width: number) => {
    localSettingsStore.setState((state) => {
      const newState = { ...state, aiSidebarWidth: width }
      saveToStorage(newState)
      return newState
    })
  },
}

// Hook for accessing selected org ID from local settings
export function useLocalSelectedOrganizationId() {
  return useStore(localSettingsStore, (state) => state.selectedOrganizationId)
}

// Hook for accessing theme preference
export function useTheme() {
  return useStore(localSettingsStore, (state) => state.theme)
}
